import baseAPI from '@/api/base';

/**
 * The support chat: conversations between an organization's people and
 * Screenkode staff — each one question, started by either side with its first
 * message and resolved by staff when it is done. Staff write from the admin's
 * Chat page, the organization's people from the floating chat in Screenkode
 * and Chabad Digital.
 *
 * What the chat components share: the API calls, the limits the API holds
 * files to, and how messages, times and people read. This module, the
 * components in components/chat and assets/style/chat.css are the same in the
 * admin and both apps — change them here, then copy them over.
 */

export type ChatSide = 'staff' | 'organization';
export const CHAT_SIDE_STAFF: ChatSide = 'staff';
export const CHAT_SIDE_ORGANIZATION: ChatSide = 'organization';

/** Where a conversation stands: being talked through, or resolved by staff (and closed to new messages). */
export type ChatStatus = 'open' | 'resolved';

/* -------------------------------- limits --------------------------------- */
// Mirrors what the API takes (its utils/consts and utils/chat_upload), so a
// bad pick is caught here instead of coming back as a 400.

export const CHAT_MESSAGE_MAX_LENGTH = 5000;
export const CHAT_SUBJECT_MAX_LENGTH = 120;
export const CHAT_RATING_COMMENT_MAX_LENGTH = 1000;
export const CHAT_FILE_MAX_FILES = 10;
export const CHAT_FILE_MAX_BYTES = 25 * 1024 * 1024;
export const CHAT_FILE_MAX_MB = Math.round(CHAT_FILE_MAX_BYTES / (1024 * 1024));
export const CHAT_FILE_EXTENSIONS = [
  '.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.heif',
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.csv', '.txt', '.rtf', '.odt', '.ods', '.odp',
  '.zip', '.mp4', '.mov', '.webm', '.m4v', '.mp3', '.m4a', '.wav', '.ogg', '.aac',
  '.log', '.json', '.xml', '.ttf', '.otf', '.woff', '.woff2',
];
export const CHAT_FILE_ACCEPT = CHAT_FILE_EXTENSIONS.join(',');
export const CHAT_VOICE_MAX_SECONDS = 5 * 60;
// How long a message may wait unread before the other side is emailed.
export const CHAT_EMAIL_DELAY_MINUTES = 5;

// The reactions one click away; any other emoji is in the picker.
export const CHAT_QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

/* ------------------------------- cadence --------------------------------- */

// What is unread (the badges, the chat button): asked this often while the tab is in view.
export const CHAT_UNREAD_POLL_MS = 30 * 1000;
// An open conversation checks for changes this often with no live connection…
export const CHAT_THREAD_POLL_MS = 4 * 1000;
// …and this often with one, as a safety net under the live notices.
export const CHAT_THREAD_LIVE_POLL_MS = 25 * 1000;
// A list of conversations.
export const CHAT_LIST_POLL_MS = 20 * 1000;
export const CHAT_PAGE_SIZE = 40;
export const CHAT_LIST_PAGE_SIZE = 50;

/* -------------------------------- shapes --------------------------------- */

export interface ChatAttachment {
  kind: 'image' | 'file' | 'voice';
  name: string;
  key?: string;
  url: string;
  size?: number;
  mimetype?: string;
  duration?: number;
  waveform?: number[];
}

export interface ChatPerson {
  id?: string | null;
  fname?: string | null;
  lname?: string | null;
  email?: string | null;
}

/** One emoji on a message and who reacted with it, as the viewer sees them ("You" first). */
export interface ChatReaction {
  emoji: string;
  count: number;
  reacted: boolean;
  names: string[];
}

export interface ChatMessage {
  id: string;
  conversationId?: string;
  organizationId?: string;
  authorSide: ChatSide;
  authorId?: string | null;
  author?: ChatPerson | null;
  body: string;
  attachments: ChatAttachment[];
  // The message this one replies to, and — as the API last saw it — its quote.
  replyToId?: string | null;
  replyTo?: ChatQuote | null;
  // A line the conversation keeps about itself rather than a message.
  event?: 'resolved' | 'reopened' | null;
  reactions?: ChatReaction[];
  clientId?: string | null;
  editedAt?: string | null;
  isDeleted?: boolean;
  createdAt: string;
  lastModifiedAt?: string;
  // Only on a message still on its way (components/chat/Chat_Thread).
  local?: boolean;
  status?: 'sending' | 'failed';
  progress?: number;
}

/** The message a reply quotes, WhatsApp-style (the API's utils/chat ChatQuote). */
export interface ChatQuote {
  id: string;
  authorSide: ChatSide;
  authorId?: string | null;
  author?: ChatPerson | null;
  preview: string;
  kind: 'text' | 'image' | 'file' | 'voice' | 'deleted';
  isDeleted?: boolean;
  thumbnail?: string | null;
}

export interface ChatLastMessage {
  id: string;
  preview: string;
  kind: 'text' | 'image' | 'file' | 'voice' | 'deleted';
  authorSide: ChatSide;
  authorId: string | null;
  authorName: string;
  isDeleted: boolean;
  createdAt: string;
}

export interface ChatConversation {
  id: string;
  organizationId: string;
  status: ChatStatus;
  // The title staff gave it, if any; `title` is what to call it either way.
  subject: string | null;
  title: string;
  resolvedAt: string | null;
  resolvedBy: ChatPerson | null;
  createdSide: ChatSide | null;
  createdBy: ChatPerson | null;
  // How the organization rated it once it was closed: 1–5 stars and/or a comment.
  rating: number | null;
  ratingComment: string | null;
  ratedAt: string | null;
  ratedBy: ChatPerson | null;
  createdAt: string;
  lastMessageAt: string | null;
  staffLastReadAt: string | null;
  organizationLastReadAt: string | null;
  organizationLastSeenAt?: string | null;
  isOnline?: boolean;
  // Staff only: who at Screenkode looks after it, who assigned it, and when (null: nobody yet).
  assignedTo?: ChatPerson | null;
  assignedBy?: ChatPerson | null;
  assignedAt?: string | null;
  unreadCount: number;
  lastMessage: ChatLastMessage | null;
  viewerSide: ChatSide;
}

/** Staff: someone at Screenkode a conversation can be assigned to, and how many open ones they look after. */
export interface ChatStaffMember extends ChatPerson {
  id: string;
  openConversations: number;
  isMe: boolean;
}

/** Staff: whose conversations a list keeps to — the viewer's, nobody's, or one staff member's id. */
export type ChatAssigneeFilter = 'me' | 'unassigned' | string;

/** A conversation in a list, with its organization — and, in a search, the message that matched. */
export interface ChatConversationRow {
  organization: any;
  conversation: ChatConversation;
  match?: {
    messageId: string;
    snippet: string;
    authorSide: ChatSide;
    authorName: string;
    matches: number;
    createdAt: string;
  } | null;
}

/* --------------------------------- API ----------------------------------- */

const CHAT_URL = '/chat';

// The side this app writes on. The apps' chat button sets 'organization': in
// an organization's app everyone is the organization, a Screenkode staff
// account that belongs to one included. The admin leaves it unset, and the
// API puts staff on their own side.
let appSide: ChatSide | null = null;

export function setChatAppSide(side: ChatSide | null): void {
  appSide = side;
}

export function chatAppSide(): ChatSide | null {
  return appSide;
}

// Sent with every request, so the API knows which side the app means.
const sideParams = (): Record<string, string> => (appSide ? { as: appSide } : {});

export interface ChatResult<T = any> {
  ok: boolean;
  data: T | null;
}

// The app's axios answers a refused request with nothing (it has already
// shown the API's message) and rejects only when there was no answer at all.
async function request<T = any>(call: Promise<any>): Promise<ChatResult<T>> {
  try {
    const response = await call;
    if (response && response.data && response.data.status) return { ok: true, data: response.data.data as T };
  } catch (error) {
    // No answer: offline, or the server is restarting. The caller retries.
  }
  return { ok: false, data: null };
}

const withoutEmpty = (params: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ''));

/**
 * Conversations, newest activity first: every organization's for staff (one
 * organization's with `organizationId`), the app's own organization's in an
 * app. `status` is 'open', 'resolved' or 'all'; `before` asks for the page
 * after the one that ended there; staff can keep to whose they are
 * (`assignee`). Answers { conversations, hasMore, counts } — and, for staff,
 * `assignment`: how many open ones are theirs and how many nobody's.
 */
export const fetchChatConversations = (params: { status?: ChatStatus | 'all'; organizationId?: string | null; before?: string | null; limit?: number; assignee?: ChatAssigneeFilter | null } = {}) =>
  request(baseAPI.get(`${CHAT_URL}/conversations`, { params: { status: 'all', ...withoutEmpty(params), ...sideParams() } }));

/** Staff: every organization, to start a conversation with. */
export const fetchChatOrganizations = () => request(baseAPI.get(`${CHAT_URL}/organizations`, { params: sideParams() }));

/** Staff: everyone at Screenkode a conversation can be assigned to (ChatStaffMember). */
export const fetchChatStaff = () => request(baseAPI.get(`${CHAT_URL}/staff`, { params: sideParams() }));

/** Staff: assigns a conversation to someone from Screenkode — or, with null, to nobody. */
export const assignChatConversation = (id: string, assigneeId: string | null) =>
  request(baseAPI.put(`${CHAT_URL}/conversation/${encodeURIComponent(id)}/assignee`, { assigneeId }, { params: sideParams() }));

/** Staff: the conversations `q` finds — by title, organization or message. */
export const fetchChatSearch = (q: string) => request(baseAPI.get(`${CHAT_URL}/search`, { params: { q, ...sideParams() } }));

/** A conversation — whole, a page of older messages (`before`), or what changed (`since`). */
export const fetchChatConversation = (params: { conversationId: string; since?: string | null; before?: string | null; limit?: number }) =>
  request(baseAPI.get(`${CHAT_URL}/conversation`, { params: { ...withoutEmpty(params), ...sideParams() } }));

/** Staff: resolve or reopen a conversation (`status`), or give it a title (`subject`; empty for none). */
export const updateChatConversation = (id: string, changes: { status?: ChatStatus; subject?: string | null }) =>
  request(baseAPI.put(`${CHAT_URL}/conversation/${encodeURIComponent(id)}`, changes, { params: sideParams() }));

/** The organization rates a closed conversation: 1–5 stars and/or a comment (what is left out stays). */
export const rateChatConversation = (id: string, feedback: { rating?: number | null; comment?: string }) =>
  request(baseAPI.put(`${CHAT_URL}/conversation/${encodeURIComponent(id)}/rating`, feedback, { params: sideParams() }));

/** What the signed-in side has waiting. */
export const fetchChatUnread = () => request(baseAPI.get(`${CHAT_URL}/unread`, { params: sideParams() }));

/** The signed-in side has read a conversation up to `upTo` (the newest message it has shown). */
export const markChatRead = (params: { conversationId: string; upTo?: string | null }) =>
  request(baseAPI.put(`${CHAT_URL}/read`, withoutEmpty(params), { params: sideParams() }));

export const editChatMessage = (id: string, body: string) =>
  request(baseAPI.put(`${CHAT_URL}/message/${encodeURIComponent(id)}`, { body }, { params: sideParams() }));

export const deleteChatMessage = (id: string) =>
  request(baseAPI.delete(`${CHAT_URL}/message/${encodeURIComponent(id)}`, { params: sideParams() }));

/** Reacts to a message with an emoji — the one already given takes it back, and none removes it. */
export const reactToChatMessage = (id: string, emoji: string | null) =>
  request(baseAPI.put(`${CHAT_URL}/message/${encodeURIComponent(id)}/reaction`, { emoji: emoji || '' }, { params: sideParams() }));

export interface ChatVoiceNote {
  blob: Blob;
  /** Seconds. */
  duration: number;
  waveform: number[];
  name: string;
}

export interface ChatOutgoing {
  // The conversation it goes to; none (with `newConversation`) starts one.
  conversationId?: string | null;
  newConversation?: boolean;
  // Staff starting a conversation name the organization.
  organizationId?: string | null;
  body: string;
  files: File[];
  voice?: ChatVoiceNote | null;
  clientId: string;
  // The message it replies to.
  replyToId?: string | null;
}

/**
 * Sends a message as a multipart form: its text, its files under `files`, a
 * voice message under `voice`. The Content-Type is set so axios sends the form
 * as it is (the app's default is JSON); the browser adds the boundary. The
 * answer carries the message and its conversation — a new one included.
 */
export const sendChatMessage = (outgoing: ChatOutgoing, onProgress?: (fraction: number) => void) => {
  const form = new FormData();
  if (outgoing.conversationId) form.append('conversationId', outgoing.conversationId);
  else if (outgoing.newConversation) form.append('newConversation', 'true');
  if (outgoing.organizationId && !outgoing.conversationId) form.append('organizationId', outgoing.organizationId);
  form.append('clientId', outgoing.clientId);
  if (outgoing.replyToId) form.append('replyToId', outgoing.replyToId);
  if (outgoing.body) form.append('body', outgoing.body);
  outgoing.files.forEach((file) => form.append('files', file, file.name));
  if (outgoing.voice) {
    form.append('voiceDuration', String(Math.round(outgoing.voice.duration * 10) / 10));
    form.append('voiceWaveform', JSON.stringify(outgoing.voice.waveform || []));
    form.append('voice', outgoing.voice.blob, outgoing.voice.name);
  }
  return request(baseAPI.post(`${CHAT_URL}/message`, form, {
    params: sideParams(),
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 5 * 60 * 1000,
    onUploadProgress: (event: { loaded: number; total?: number }) => {
      if (onProgress && event.total) onProgress(Math.min(event.loaded / event.total, 1));
    },
  }));
};

/** An id the app gives a message before sending it, so a retried send is never saved twice. */
export function chatClientId(): string {
  const cryptoApi: any = typeof window !== 'undefined' ? (window as any).crypto : null;
  const random = cryptoApi && typeof cryptoApi.randomUUID === 'function'
    ? cryptoApi.randomUUID().replace(/-/g, '')
    : `${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
  return `c${Date.now().toString(36)}${random}`.slice(0, 48);
}

/* -------------------------------- drafts --------------------------------- */

// What was typed and not sent, per conversation, for as long as the page is open.
const drafts = new Map<string, string>();

export const chatDrafts = {
  get: (key: string): string => drafts.get(key) || '',
  set: (key: string, value: string): void => {
    if (value) drafts.set(key, value);
    else drafts.delete(key);
  },
};

/* --------------------------------- files --------------------------------- */

export function fileExtension(name?: string | null): string {
  const match = /\.[^./\\]+$/.exec(String(name || ''));
  return match ? match[0].toLowerCase() : '';
}

export function formatFileSize(bytes?: number | null): string {
  const size = Number(bytes) || 0;
  if (!size) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(size < 10 * 1024 * 1024 ? 1 : 0)} MB`;
}

/** Whether this browser can record a voice message: a microphone API, and a recorder. */
export function canRecordVoice(): boolean {
  return typeof window !== 'undefined'
    && typeof (window as any).MediaRecorder !== 'undefined'
    && Boolean(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
}

/** "0:07", "1:05". */
export function formatChatDuration(seconds?: number | null): string {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];

/** A picture every browser can draw (not HEIC). */
export function isChatImage(name?: string | null, type?: string | null): boolean {
  return IMAGE_EXTENSIONS.includes(fileExtension(name)) || /^image\/(jpeg|png|gif|webp)$/i.test(String(type || ''));
}

/** The primeicons glyph for a file of this kind. */
export function chatFileIcon(file: { name?: string | null; mimetype?: string | null; type?: string | null }): string {
  const name = String(file.name || '');
  const type = String(file.mimetype || file.type || '');
  const extension = fileExtension(name);
  if (isChatImage(name, type) || ['.heic', '.heif'].includes(extension)) return 'pi-image';
  if (extension === '.pdf') return 'pi-file-pdf';
  if (['.xls', '.xlsx', '.csv', '.ods'].includes(extension)) return 'pi-file-excel';
  if (['.doc', '.docx', '.rtf', '.odt'].includes(extension)) return 'pi-file-word';
  if (['.mp4', '.mov', '.webm', '.m4v'].includes(extension) || /^video\//.test(type)) return 'pi-video';
  if (['.mp3', '.m4a', '.wav', '.ogg', '.aac'].includes(extension) || /^audio\//.test(type)) return 'pi-volume-up';
  if (extension === '.zip') return 'pi-folder';
  return 'pi-file';
}

/** Why a picked file cannot be sent, or null when it can. */
export function chatFileProblem(file: File): string | null {
  if (!CHAT_FILE_EXTENSIONS.includes(fileExtension(file.name))) {
    return `"${file.name}" can't be sent. Pictures, PDFs, documents, spreadsheets, audio, video, text and zip files can.`;
  }
  if (file.size > CHAT_FILE_MAX_BYTES) return `"${file.name}" is larger than ${CHAT_FILE_MAX_MB}MB.`;
  if (!file.size) return `"${file.name}" is empty.`;
  return null;
}

/** The files to keep after a pick: each once, each acceptable, within the limit — and why any were left out. */
export function acceptChatFiles(current: File[], picked: ArrayLike<File> | null | undefined): { files: File[]; errors: string[] } {
  const files = [...current];
  const errors: string[] = [];
  const tooMany = `Up to ${CHAT_FILE_MAX_FILES} files can be sent with one message.`;

  Array.from(picked || []).forEach((file) => {
    if (files.some((kept) => kept.name === file.name && kept.size === file.size && kept.lastModified === file.lastModified)) return;
    const problem = chatFileProblem(file);
    if (problem) {
      errors.push(problem);
      return;
    }
    if (files.length >= CHAT_FILE_MAX_FILES) {
      if (!errors.includes(tooMany)) errors.push(tooMany);
      return;
    }
    files.push(file);
  });

  return { files, errors };
}

/* -------------------------------- people --------------------------------- */

function capitalize(value?: string | null): string {
  const text = String(value || '').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
}

/** "Moshe Levi" — the person's name, else their email, else the fallback. */
export function chatPersonName(person?: ChatPerson | null, fallback = 'Someone'): string {
  const name = [capitalize(person?.fname), capitalize(person?.lname)].filter(Boolean).join(' ');
  return name || String(person?.email || '').trim() || fallback;
}

export function chatFirstName(person?: ChatPerson | null, fallback = 'Someone'): string {
  return capitalize(person?.fname) || chatPersonName(person, fallback).split(' ')[0];
}

/** Two-letter initials for an avatar ("Beth Israel" → "BI", "Chabad" → "CH"). */
export function chatInitials(name?: string | null): string {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const first = Array.from(parts[0])[0] || '';
  const second = parts.length > 1 ? Array.from(parts[1])[0] || '' : Array.from(parts[0])[1] || '';
  return `${first}${second}`.toUpperCase();
}

/** A stable tint (0–5) for a name, so an avatar keeps its colour everywhere. */
export function chatAvatarTone(name?: string | null): number {
  const text = String(name || '');
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) hash = (hash + text.charCodeAt(index) * (index + 1)) % 997;
  return hash % 6;
}

/* --------------------------------- time ---------------------------------- */

export function chatDate(value?: string | number | Date | null): Date | null {
  if (value == null || value === '') return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function daysBetween(date: Date, now: Date): number {
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Math.round((startOf(now) - startOf(date)) / 86400000);
}

/** "03:12 PM". */
export function chatClock(value?: string | number | Date | null): string {
  const date = chatDate(value);
  return date ? date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '';
}

/** "Tuesday, Oct 1, 2026 at 3:12 PM" — a message's full time, for its tooltip. */
export function chatFullTime(value?: string | number | Date | null): string {
  const date = chatDate(value);
  if (!date) return '';
  return `${date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })} at ${chatClock(date)}`;
}

/** The separator between two days of a conversation: "Today", "Yesterday", "Monday, September 29". */
export function chatDayLabel(value?: string | number | Date | null): string {
  const date = chatDate(value);
  if (!date) return '';
  const now = new Date();
  const days = daysBetween(date, now);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return date.toLocaleDateString('en-US', date.getFullYear() === now.getFullYear()
    ? { weekday: 'long', month: 'long', day: 'numeric' }
    : { month: 'long', day: 'numeric', year: 'numeric' });
}

/** A conversation list's time: "3:12 PM" today, "Yesterday", "Mon" this week, "Sep 29", "Sep 29, 2025". */
export function chatListTime(value?: string | number | Date | null): string {
  const date = chatDate(value);
  if (!date) return '';
  const now = new Date();
  const days = daysBetween(date, now);
  if (days <= 0) return chatClock(date);
  if (days === 1) return 'Yesterday';
  if (days < 7) return date.toLocaleDateString('en-US', { weekday: 'short' });
  return date.toLocaleDateString('en-US', date.getFullYear() === now.getFullYear()
    ? { month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' });
}

/** How long ago, as a messenger's list says it: "now", "5m", "3h", "2d", "3w", "4mo", "1y". */
export function chatAgo(value?: string | number | Date | null): string {
  const date = chatDate(value);
  if (!date) return '';
  const seconds = Math.max(0, (Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  if (days < 30) return `${Math.floor(days / 7)}w`;
  if (days < 365) return `${Math.max(1, Math.floor(days / 30))}mo`;
  return `${Math.floor(days / 365)}y`;
}

/** "Active now", "Active 5m ago", "Active 3h ago", "Active Sep 29" — or '' when never seen. */
export function chatPresenceLabel(isOnline?: boolean, lastSeen?: string | null): string {
  if (isOnline) return 'Active now';
  const date = chatDate(lastSeen);
  if (!date) return '';
  const minutes = Math.max(1, Math.round((Date.now() - date.getTime()) / 60000));
  if (minutes < 60) return `Active ${minutes}m ago`;
  if (minutes < 24 * 60) return `Active ${Math.round(minutes / 60)}h ago`;
  return `Active ${chatListTime(date)}`;
}

/* -------------------------------- quotes --------------------------------- */

/**
 * A message as a reply quotes it: its text, or what it sent — "Voice message
 * (0:12)", "3 photos", "report.pdf" — with its first picture as a thumbnail.
 * The same rules as the API's quote (utils/chat chatQuoteOf), worked out here
 * from a message already loaded, so an edit or a delete shows at once.
 */
export function chatQuoteOf(message: ChatMessage): ChatQuote {
  const deleted = Boolean(message.isDeleted);
  const files = deleted ? [] : (Array.isArray(message.attachments) ? message.attachments : []);
  const voice = files.find((file) => file.kind === 'voice');
  const images = files.filter((file) => file.kind === 'image');
  const text = String(message.body || '').replace(/\s+/g, ' ').trim();

  let preview = '';
  if (deleted) preview = 'Message deleted';
  else if (text) preview = text.length <= 120 ? text : `${text.slice(0, 119).trimEnd()}…`;
  else if (voice) preview = voice.duration ? `Voice message (${formatChatDuration(voice.duration)})` : 'Voice message';
  else if (images.length && images.length === files.length) preview = images.length === 1 ? 'Photo' : `${images.length} photos`;
  else if (files.length === 1) preview = files[0].name;
  else if (files.length) preview = `${files.length} files`;

  let kind: ChatQuote['kind'] = 'text';
  if (deleted) kind = 'deleted';
  else if (voice) kind = 'voice';
  else if (!text && files.length) kind = images.length === files.length ? 'image' : 'file';

  return {
    id: message.id,
    authorSide: message.authorSide,
    authorId: message.authorId || null,
    author: message.author || null,
    preview,
    kind,
    isDeleted: deleted,
    thumbnail: images.length ? images[0].url : null,
  };
}

/* ------------------------------- reactions ------------------------------- */

/**
 * A message's reactions once the viewer picks `emoji` — the API's rule
 * (utils/chat applyChatReaction), worked out here so the change shows at
 * once: it replaces the viewer's reaction, and picking the one they had (or
 * none) takes it back.
 */
export function chatReactionsAfter(reactions: ChatReaction[] | null | undefined, emoji: string | null): ChatReaction[] {
  const list = Array.isArray(reactions) ? reactions : [];
  const had = list.find((group) => group.reacted) || null;
  const next = list
    .map((group) => (group.reacted
      ? { ...group, count: group.count - 1, reacted: false, names: group.names.filter((name) => name !== 'You') }
      : { ...group, names: [...group.names] }))
    .filter((group) => group.count > 0);
  if (!emoji || (had && had.emoji === emoji)) return next;
  const existing = next.find((group) => group.emoji === emoji);
  if (existing) {
    existing.count += 1;
    existing.reacted = true;
    existing.names = ['You', ...existing.names];
    return next;
  }
  return [...next, { emoji, count: 1, reacted: true, names: ['You'] }];
}

/** "You, Abel and Sara Katz reacted with ❤️". */
export function chatReactionLabel(group: ChatReaction): string {
  const names = group.names.length ? group.names : ['Someone'];
  const people = names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0];
  return `${people} reacted with ${group.emoji}`;
}

/* --------------------------------- text ---------------------------------- */

export interface ChatTextPart {
  text: string;
  href?: string;
}

const LINK = /\b((?:https?:\/\/|www\.)[^\s<>"]*[^\s<>".,:;!?'")\]}])/gi;

/** A message's text in pieces, its web addresses as links — rendered as text, never as HTML. */
export function chatTextParts(text?: string | null): ChatTextPart[] {
  const value = String(text || '');
  const parts: ChatTextPart[] = [];
  let last = 0;
  value.replace(LINK, (match: string, _link: string, offset: number) => {
    if (offset > last) parts.push({ text: value.slice(last, offset) });
    parts.push({ text: match, href: /^https?:\/\//i.test(match) ? match : `https://${match}` });
    last = offset + match.length;
    return match;
  });
  if (last < value.length) parts.push({ text: value.slice(last) });
  return parts;
}

const EMOJI_SEQUENCE = /\p{Extended_Pictographic}(?:️|︎)?\p{Emoji_Modifier}?(?:‍\p{Extended_Pictographic}(?:️)?\p{Emoji_Modifier}?)*/gu;

/** How many emoji a message is made of when it is nothing else (1–3 are shown large), else 0. */
export function chatEmojiOnlyCount(text?: string | null): number {
  const compact = String(text || '').replace(/\s+/g, '');
  if (!compact || compact.length > 32) return 0;
  const matches = compact.match(EMOJI_SEQUENCE);
  if (!matches || matches.join('') !== compact) return 0;
  return matches.length;
}

/** A message as one line, with who sent it as the viewer knows them: "You: …", "Abel: …". */
export function chatPreviewText(lastMessage?: ChatLastMessage | null, options: { viewerId?: string | null; viewerSide?: ChatSide; theirName?: string } = {}): string {
  if (!lastMessage) return '';
  const preview = lastMessage.preview || '';
  if (lastMessage.isDeleted) return preview;
  const mine = lastMessage.authorId && options.viewerId && lastMessage.authorId === options.viewerId
    && (!options.viewerSide || lastMessage.authorSide === options.viewerSide);
  if (mine) return `You: ${preview}`;
  const name = String(lastMessage.authorName || options.theirName || '').trim().split(' ')[0];
  return name ? `${name}: ${preview}` : preview;
}
